/**
 * Completely dependency-free YouMayAlsoLikeComponent.
 * Uses manual event construction to avoid import-map resolution issues.
 */
class YouMayAlsoLikeComponent extends HTMLElement {
  constructor() {
    super();
    // Static event reference to match theme's events.js
    this.CART_UPDATE_EVENT = 'cart:update';
    this.CART_ERROR_EVENT = 'cart:error';
  }

  connectedCallback() {
    this.addEventListener('change', this.#handleChange.bind(this));
    this.addEventListener('submit', this.#handleAddToCart.bind(this));
    this.addEventListener('click', this.#handleClick.bind(this));

    // Safety: ensure forms don't redirect
    this.querySelectorAll('form').forEach(form => {
      form.onsubmit = (e) => { e.preventDefault(); return false; };
    });

    // Listen to cart update events to restore temporary success states after morphing
    this.cartUpdateListener = () => {
      setTimeout(() => {
        this.#updateButtonSuccessStates();
      }, 100);
    };
    document.addEventListener(this.CART_UPDATE_EVENT, this.cartUpdateListener);
  }

  disconnectedCallback() {
    if (this.cartUpdateListener) {
      document.removeEventListener(this.CART_UPDATE_EVENT, this.cartUpdateListener);
    }
  }

  #handleChange(e) {
    const select = e.target;
    if (select.hasAttribute('data-ymal-size-select')) {
      this.#selectSize(select);
    }
  }

  #handleClick(e) {
    const addBtn = e.target.closest('.you-may-also-like__add-btn');
    if (addBtn) {
      e.preventDefault();
      this.#handleDesktopAddToCart(addBtn);
      return;
    }

    const swatch = e.target.closest('.pcs-block__swatch');
    if (swatch && swatch.closest('you-may-also-like-component') === this) {
      e.preventDefault();
      this.#selectColorSwatch(swatch);
    }

    // Close dropdown if clicking outside or on a different dropdown
    const clickedDropdown = e.target.closest('.ymal-custom-dropdown');
    this.querySelectorAll('.ymal-custom-dropdown[open]').forEach(details => {
      if (details !== clickedDropdown) {
        details.removeAttribute('open');
      }
    });
  }

  async #handleDesktopAddToCart(btn) {
    const card = btn.closest('.ymal-row-wrap');
    if (!card) return;

    const sizeSelect = card.querySelector('[data-ymal-size-select]');
    const variantId = sizeSelect ? sizeSelect.value : null;

    if (!variantId) return;

    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    // Set success state immediately on click for instant visual feedback
    btn.setAttribute('data-added', 'true');
    window.recentlyAddedVariantIds = window.recentlyAddedVariantIds || {};
    window.recentlyAddedVariantIds[variantId.toString()] = Date.now();

    try {
      const formData = new FormData();
      formData.append('id', variantId.toString());
      formData.append('quantity', '1');

      /** @type {string[]} */
      const sectionsToUpdate = [];
      /** @type {NodeListOf<HTMLElement>} */
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach(comp => {
        const sId = comp.dataset.sectionId;
        if (sId && !sectionsToUpdate.includes(sId)) {
          sectionsToUpdate.push(sId);
        }
      });
      if (sectionsToUpdate.length === 0) sectionsToUpdate.push('header');
      formData.append('sections', sectionsToUpdate.join(','));

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      const itemAdded = await response.json();

      if (!response.ok) {
        throw new Error(itemAdded.description || itemAdded.message || 'Error adding to cart');
      }

      // Fetch full cart for tracking and UI sync
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      setTimeout(() => {
        btn.removeAttribute('data-added');
      }, 1500);

      // Manually construct the event to match the structure in events.js
      const source = this.dataset.source || 'you-may-also-like';
      const eventDetail = {
        resource: cart,
        sourceId: source,
        data: {
          sections: itemAdded.sections,
          source: source,
          itemCount: cart.item_count,
          variantId: itemAdded.variant_id,
          productTitle: itemAdded.product_title || itemAdded.title,
          productImage: itemAdded.image
        }
      };

      document.dispatchEvent(new CustomEvent(this.CART_UPDATE_EVENT, {
        bubbles: true,
        detail: eventDetail
      }));

    } catch (err) {
      btn.removeAttribute('data-added');
      if (variantId) {
        delete window.recentlyAddedVariantIds[variantId.toString()];
      }
      console.error('Add to cart error:', err);
      document.dispatchEvent(new CustomEvent(this.CART_ERROR_EVENT, {
        bubbles: true,
        detail: {
          sourceId: 'you-may-also-like',
          data: { message: err.message }
        }
      }));
    } finally {
      btn.classList.remove('is-loading');
    }
  }

  #selectColorSwatch(swatch) {
    const handle = swatch.dataset.handle;
    const card = swatch.closest('.ymal-row-wrap');
    if (!card || !handle) return;

    // 1a. Update active swatch class for the container
    const swatchContainer = swatch.parentElement;
    if (swatchContainer) {
      swatchContainer.querySelectorAll('.pcs-block__swatch').forEach(s => {
        s.classList.remove('pcs-block__swatch--active');
      });
      swatch.classList.add('pcs-block__swatch--active');
    }

    // 1b. Update Custom Dropdown UI + Close Dropdown
    const dropdown = swatch.closest('details');
    if (dropdown) {
      const activeSummaryContainer = dropdown.querySelector('.ymal-custom-dropdown__summary');
      if (activeSummaryContainer) {
        // Find dot of clicked item and copy it
        const dotContainer = swatch.querySelector('.ymal-color-select-dot');
        const activeDot = activeSummaryContainer.querySelector('.ymal-color-select-dot');
        const activeLabel = activeSummaryContainer.querySelector('.ymal-custom-dropdown__label');
        if (dotContainer && activeDot) {
          activeDot.innerHTML = dotContainer.innerHTML;
        }
        if (activeLabel) {
          activeLabel.textContent = swatch.dataset.colorLabel || handle;
        }
      }
      dropdown.removeAttribute('open');
    }

    const dataEl = this.querySelector(`script[data-ymal-data="${handle}"]`);
    if (!dataEl) return;

    const data = JSON.parse(dataEl.textContent);

    // 2. Update Card UI
    if (data.id) {
      card.dataset.productId = data.id;
      const wishlistBtn = card.querySelector('.swym-add-to-wishlist-view-product');
      if (wishlistBtn) {
        wishlistBtn.dataset.productId = data.id;
        // The swym button relies on the product_ID class. Let's find and replace it
        wishlistBtn.className = wishlistBtn.className.replace(/product_\d+/, `product_${data.id}`);
      }
    }

    if (data.title) {
      const cardTitle = card.querySelector('.cart-items__product-info a');
      if (cardTitle) cardTitle.textContent = data.title;
      const quickAdd = card.querySelector('.you-may-also-like__quick-add');
      if (quickAdd) quickAdd.setAttribute('data-product-title', data.title);
      const quickAddMobile = card.querySelector('.you-may-also-like__add-overlay-wrapper');
      if (quickAddMobile) quickAddMobile.setAttribute('data-product-title', data.title);
    }
    if (data.vendor) {
      const cardVendor = card.querySelector('.cart-items__product-brand p');
      if (cardVendor) cardVendor.textContent = data.vendor;
    }
    if (data.price) {
      const priceHtml = `<text-component value="${data.price.replace(/<[^>]+>/g, '')}">${data.price}</text-component>`;
      const priceContainer = card.querySelector('.cart-items__price');
      if (priceContainer) {
        priceContainer.innerHTML = priceHtml;
      }
    }
    if (data.media) {
      const cardImage = card.querySelector('.cart-items__media-image');
      if (cardImage) {
        cardImage.src = data.media;
        cardImage.removeAttribute('srcset');
      }
    }

    if (data.promo_tags !== undefined) {
      const promoContainer = card.querySelector('.product-promo-badges');
      if (data.promo_tags && data.promo_tags.trim() !== '') {
        if (!promoContainer) {
          const titleEl = card.querySelector('.cart-items__product-info');
          if (titleEl) {
            titleEl.insertAdjacentHTML('afterend', data.promo_tags);
          }
        } else {
          promoContainer.outerHTML = data.promo_tags;
        }
      } else if (promoContainer) {
        promoContainer.remove();
      }
    }

    const cardLinks = card.querySelectorAll('a[href]');
    cardLinks.forEach(l => l.href = data.url);

    const cardLinkInput = card.querySelector('input.product-card__link');
    if (cardLinkInput) cardLinkInput.value = data.url;

    // Update Size Select
    const sizeSelect = card.querySelector('[data-ymal-size-select]');
    if (sizeSelect && data.variants) {
      let html = '';
      let firstAvailableId = null;

      data.variants.forEach((v) => {
        if (v.available && firstAvailableId === null) firstAvailableId = v.id;
        html += `<option value="${v.id}" ${v.available ? '' : 'disabled'}>${v.title}</option>`;
      });

      sizeSelect.innerHTML = html;
      sizeSelect.value = firstAvailableId || data.variants[0].id;

      // Update form hidden input
      const variantInput = card.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = sizeSelect.value;
    }
  }

  #selectSize(select) {
    const card = select.closest('.ymal-row-wrap');
    const variantInput = card.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = select.value;
  }

  #updateButtonSuccessStates() {
    const recentlyAdded = window.recentlyAddedVariantIds || {};
    const now = Date.now();

    const cards = this.querySelectorAll('.ymal-row-wrap');
    cards.forEach(card => {
      const sizeSelect = card.querySelector('[data-ymal-size-select]');
      const variantInput = card.querySelector('input[name="id"]');
      const variantId = (sizeSelect ? sizeSelect.value : variantInput?.value)?.toString();

      if (!variantId) return;

      const timestamp = recentlyAdded[variantId];
      if (timestamp && (now - timestamp < 1500)) {
        const btn = card.querySelector('.you-may-also-like__add-btn');
        if (btn) {
          btn.setAttribute('data-added', 'true');
          const remaining = 1500 - (now - timestamp);
          setTimeout(() => {
            btn.removeAttribute('data-added');
          }, remaining);
        }
      }
    });
  }

  async #handleAddToCart(e) {
    const form = e.target;
    if (!form.getAttribute('action')?.includes('/cart/add')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const btn = form.querySelector('button');
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    const variantId = form.querySelector('input[name="id"]')?.value;

    // Set success state immediately on click/submit for instant visual feedback
    btn.setAttribute('data-added', 'true');
    if (variantId) {
      window.recentlyAddedVariantIds = window.recentlyAddedVariantIds || {};
      window.recentlyAddedVariantIds[variantId.toString()] = Date.now();
    }

    try {
      const formData = new FormData(form);
      /** @type {string[]} */
      const sectionsToUpdate = [];
      /** @type {NodeListOf<HTMLElement>} */
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach(comp => {
        const sId = comp.dataset.sectionId;
        if (sId && !sectionsToUpdate.includes(sId)) {
          sectionsToUpdate.push(sId);
        }
      });
      if (sectionsToUpdate.length === 0) sectionsToUpdate.push('header');
      formData.append('sections', sectionsToUpdate.join(','));

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      const itemAdded = await response.json();

      if (!response.ok) {
        throw new Error(itemAdded.description || itemAdded.message || 'Error adding to cart');
      }

      // Fetch full cart for tracking and UI sync
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();

      setTimeout(() => {
        btn.removeAttribute('data-added');
      }, 1500);

      // Manually construct the event to match the structure in events.js
      const source = this.dataset.source || 'you-may-also-like';
      const eventDetail = {
        resource: cart,
        sourceId: source,
        data: {
          sections: itemAdded.sections,
          source: source,
          itemCount: cart.item_count,
          variantId: itemAdded.variant_id,
          productTitle: itemAdded.product_title || itemAdded.title,
          productImage: itemAdded.image
        }
      };

      document.dispatchEvent(new CustomEvent(this.CART_UPDATE_EVENT, {
        bubbles: true,
        detail: eventDetail
      }));

    } catch (err) {
      btn.removeAttribute('data-added');
      if (variantId) {
        delete window.recentlyAddedVariantIds[variantId.toString()];
      }
      console.error('Add to cart error:', err);
      document.dispatchEvent(new CustomEvent(this.CART_ERROR_EVENT, {
        bubbles: true,
        detail: {
          sourceId: 'you-may-also-like',
          data: { message: err.message }
        }
      }));
    } finally {
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('you-may-also-like-component')) {
  customElements.define('you-may-also-like-component', YouMayAlsoLikeComponent);
}

// ─── Mobile cart-drawer "YOU MAY ALSO LIKE" toggle ───────────────────────────
// Zero changes to liquid. Everything is injected by JS only when inside
// .also-like-products--mobile (mobile cart drawer). Desktop is untouched.
(function () {
  var CSS_ID = 'ymal-mobile-toggle-css';

  function injectCSS() {
    if (document.getElementById(CSS_ID)) return;
    var s = document.createElement('style');
    s.id = CSS_ID;
    s.textContent = [
      '.also-like-products--mobile{position:fixed !important;bottom:220px !important;z-index:5 !important;background:#F8F8F8 !important;border-top:1px solid #E2E2E2 !important;margin:0 0px 0 -16px !important;padding:0 !important;box-sizing:border-box !important;max-width:100vw !important;}',
      '.customer.also-like-products--mobile{bottom:250px !important;}',
      '@media(min-width:750px){',
      '.also-like-products--mobile{margin:0 -24px 0 -24px !important;}',
      '.also-like-products--mobile .ymal-toggle-hd{padding:10px 24px !important;}',
      '.also-like-products--mobile .ymal-bd-in{padding:0 24px 14px 24px !important;}',
      '}',
      '.cart-drawer__items--cart .cart-items__table tbody{padding-bottom:100px !important;}',
      '.cart-drawer__items--cart .cart-items__table-row:last-child, .cart-drawer__items--cart .cart-items__table-row:last-of-type{padding-bottom:20px !important;}',
      '.also-like-products--mobile .you-may-also-like{padding:0 !important;margin:0 !important;background:transparent !important;border-top:none !important;box-sizing:border-box !important;}',
      '.also-like-products--mobile .ymal-toggle-hd{display:flex !important;align-items:center !important;justify-content:space-between !important;gap:8px !important;padding:10px 16px !important;cursor:pointer !important;-webkit-tap-highlight-color:transparent !important;user-select:none !important;width:100% !important;box-sizing:border-box !important;}',
      '.also-like-products--mobile .you-may-also-like__title{margin:0 !important;margin-bottom:0 !important;padding:0 !important;font-size:13px !important;font-weight:600 !important;line-height:1.4 !important;letter-spacing:0.04em !important;text-transform:uppercase !important;min-width:0 !important;flex:1 1 auto !important;overflow:hidden !important;text-overflow:ellipsis !important;white-space:nowrap !important;}',
      '.also-like-products--mobile .ymal-tb{width:24px !important;height:24px !important;border:none !important;outline:none !important;background:transparent !important;display:flex !important;align-items:center !important;justify-content:center !important;cursor:pointer !important;padding:0 !important;flex-shrink:0 !important;border-radius:0 !important;box-sizing:border-box !important;}',
      '.also-like-products--mobile .ymal-tb .mi{display:none !important;} .also-like-products--mobile .ymal-tb .pl{display:flex !important;}',
      '.also-like-products--mobile.ymal-open .ymal-tb .mi{display:flex !important;} .also-like-products--mobile.ymal-open .ymal-tb .pl{display:none !important;}',
      '.also-like-products--mobile .ymal-bd{overflow:hidden !important;transition:max-height .45s cubic-bezier(0.16, 1, 0.3, 1) !important;width:100% !important;box-sizing:border-box !important;will-change:max-height;}',
      '.also-like-products--mobile:not(.ymal-open) .ymal-bd{max-height:60px !important;}',
      '.also-like-products--mobile.ymal-open .ymal-bd{max-height:480px;-webkit-mask-image:none !important;mask-image:none !important;}',
      '.also-like-products--mobile .ymal-bd-in{padding:0 16px 14px 16px !important;box-sizing:border-box !important;}',
      '@media(max-width:367px){',
      '.also-like-products--mobile{bottom:240px !important;}',
      '.customer.also-like-products--mobile{bottom:266px !important;}',
      '.also-like-products--mobile .you-may-also-like__title{font-size:12px !important;}',
      '}'
    ].join('');
    document.head.appendChild(s);
  }

  function buildToggleHeader(titleText) {
    var hd = document.createElement('div');
    hd.className = 'ymal-toggle-hd';
    hd.innerHTML =
      '<span class="you-may-also-like__title">' + titleText + '</span>' +
      '<button type="button" class="ymal-tb" aria-label="Toggle you may also like">' +
      '<svg class="pl" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M1 6H11M6 1V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '<svg class="mi" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M1 6H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
      '</button>';
    return hd;
  }

  function isScrolledToBottom(container) {
    if (!container) return false;

    // 1. Container must be scrollable (enough cart items to create scrolling)
    var maxScroll = container.scrollHeight - container.clientHeight;
    if (maxScroll <= 2) return false;

    // 2. Container has reached the end of its scrollable content
    var isContainerAtBottom = (container.scrollTop + container.clientHeight) >= (container.scrollHeight - 3);
    if (!isContainerAtBottom) return false;

    // 3. The last cart item (.cart-items__table-row) has reached the visible/end position
    var rows = container.querySelectorAll('.cart-items__table-row, .cart-item');
    if (!rows.length) return false;

    var lastRow = rows[rows.length - 1];
    var containerRect = container.getBoundingClientRect();
    var lastRowRect = lastRow.getBoundingClientRect();

    return lastRowRect.bottom <= (containerRect.bottom + 3);
  }

  function updateYmalState(container, mobileWrapper) {
    if (!container || !mobileWrapper) return;

    var isOpen = mobileWrapper.classList.contains('ymal-open');
    var isUserClosed = mobileWrapper.dataset.userClosed === 'true';
    var atBottom = isScrolledToBottom(container);

    if (atBottom) {
      if (!isOpen && !isUserClosed) {
        var bd = mobileWrapper.querySelector('.ymal-bd');
        var bdi = mobileWrapper.querySelector('.ymal-bd-in');
        if (bd && bdi) {
          var targetH = (bdi.scrollHeight || bdi.offsetHeight || 360) + 16;
          bd.style.maxHeight = targetH + 'px';
        }
        mobileWrapper.classList.add('ymal-open');
        var drawerCartItems = mobileWrapper.closest('.cart-drawer__items--cart');
        if (drawerCartItems) {
          drawerCartItems.classList.add('ymal-cart-open');
        }
      }
    } else {
      delete mobileWrapper.dataset.userClosed;
      if (isOpen) {
        mobileWrapper.classList.remove('ymal-open');
        var bd = mobileWrapper.querySelector('.ymal-bd');
        if (bd) {
          bd.style.maxHeight = '';
        }
        var drawerCartItems = mobileWrapper.closest('.cart-drawer__items--cart');
        if (drawerCartItems) {
          drawerCartItems.classList.remove('ymal-cart-open');
        }
      }
    }
  }

  function setupScrollListener(mobileWrapper) {
    // Scroll listeners on cart containers (.cart-drawer__items--cart, .cart-items__wrapper)
    var containers = [
      mobileWrapper.closest('.cart-drawer__items--cart'),
      mobileWrapper.closest('.cart-items__wrapper'),
      document.querySelector('.cart-drawer__items.cart-drawer__items--cart'),
      document.querySelector('.cart-items__wrapper')
    ].filter(function (el, idx, self) {
      return el && self.indexOf(el) === idx;
    });

    containers.forEach(function (container) {
      if (container.dataset.scrollBound) return;
      container.dataset.scrollBound = '1';

      var handleScrollCheck = function () {
        updateYmalState(container, mobileWrapper);
      };

      container.addEventListener('scroll', handleScrollCheck, { passive: true });
      container.addEventListener('touchmove', handleScrollCheck, { passive: true });
      container.addEventListener('wheel', handleScrollCheck, { passive: true });

      // Run initial check on open/bind
      handleScrollCheck();
    });

    // Touch swipe listener on mobileWrapper itself (collapses when swiping down on recommendations)
    if (!mobileWrapper.dataset.touchBound) {
      mobileWrapper.dataset.touchBound = '1';
      var touchStartY = 0;
      var touchStartX = 0;
      var initialScrollTop = 0;

      mobileWrapper.addEventListener('touchstart', function (e) {
        if (e.touches && e.touches[0]) {
          touchStartY = e.touches[0].clientY;
          touchStartX = e.touches[0].clientX;
          var container = mobileWrapper.closest('.cart-drawer__items--cart') || document.querySelector('.cart-drawer__items.cart-drawer__items--cart');
          if (container) {
            initialScrollTop = container.scrollTop;
          }
        }
      }, { passive: true });

      mobileWrapper.addEventListener('touchmove', function (e) {
        if (!mobileWrapper.classList.contains('ymal-open')) return;
        if (!e.touches || !e.touches[0]) return;

        var currentY = e.touches[0].clientY;
        var currentX = e.touches[0].clientX;
        var diffY = currentY - touchStartY;
        var diffX = Math.abs(currentX - touchStartX);

        // If swiping downwards vertically significantly more than horizontally
        if (diffY > 10 && diffY > diffX * 1.0) {
          delete mobileWrapper.dataset.userClosed;
          mobileWrapper.classList.remove('ymal-open');
          var bd = mobileWrapper.querySelector('.ymal-bd');
          if (bd) {
            bd.style.maxHeight = '';
          }
          var drawerCartItems = mobileWrapper.closest('.cart-drawer__items--cart');
          if (drawerCartItems) {
            drawerCartItems.classList.remove('ymal-cart-open');
          }

          var container = mobileWrapper.closest('.cart-drawer__items--cart') || document.querySelector('.cart-drawer__items.cart-drawer__items--cart');
          if (container) {
            container.scrollTop = Math.max(0, initialScrollTop - diffY);
          }
        }
      }, { passive: true });
    }

    // Mouse wheel scroll listener on mobileWrapper (collapses on wheel UP)
    if (!mobileWrapper.dataset.wheelBound) {
      mobileWrapper.dataset.wheelBound = '1';

      mobileWrapper.addEventListener('wheel', function (e) {
        var isOpen = mobileWrapper.classList.contains('ymal-open');
        var bd = mobileWrapper.querySelector('.ymal-bd');

        if (e.deltaY < -5 && isOpen) {
          delete mobileWrapper.dataset.userClosed;
          mobileWrapper.classList.remove('ymal-open');
          if (bd) bd.style.maxHeight = '';
          var drawerCartItems = mobileWrapper.closest('.cart-drawer__items--cart');
          if (drawerCartItems) {
            drawerCartItems.classList.remove('ymal-cart-open');
          }
          var container = mobileWrapper.closest('.cart-drawer__items--cart') || document.querySelector('.cart-drawer__items.cart-drawer__items--cart');
          if (container) {
            container.scrollTop = Math.max(0, container.scrollTop + e.deltaY);
          }
        } else if (e.deltaY > 5) {
          var container = mobileWrapper.closest('.cart-drawer__items--cart') || document.querySelector('.cart-drawer__items.cart-drawer__items--cart');
          if (container) {
            updateYmalState(container, mobileWrapper);
          }
        }
      }, { passive: true });
    }
  }

  function initToggle(mobileWrapper) {
    var ymal = mobileWrapper.querySelector('.you-may-also-like');
    if (!ymal || ymal.dataset.mToggle) return;
    ymal.dataset.mToggle = '1';

    var title = ymal.querySelector('.you-may-also-like__title');
    var list = ymal.querySelector('.you-may-also-like__list');
    if (!title || !list) return;

    injectCSS();

    // Build toggle header and collapsible wrapper
    var hd = buildToggleHeader(title.textContent.trim());
    var bd = document.createElement('div'); bd.className = 'ymal-bd';
    var bdi = document.createElement('div'); bdi.className = 'ymal-bd-in';

    // Restructure: header + collapsible body (move list inside)
    title.parentNode.insertBefore(hd, title);
    title.remove();
    bdi.appendChild(list);       // move original list (all controls intact)
    bd.appendChild(bdi);
    ymal.appendChild(bd);

    // Toggle on header click
    hd.addEventListener('click', function () {
      var isCurrentlyOpen = mobileWrapper.classList.contains('ymal-open');
      var bd = mobileWrapper.querySelector('.ymal-bd');
      var bdi = mobileWrapper.querySelector('.ymal-bd-in');

      if (isCurrentlyOpen) {
        mobileWrapper.classList.remove('ymal-open');
        mobileWrapper.dataset.userClosed = 'true';
        if (bd) bd.style.maxHeight = '';
      } else {
        if (bd && bdi) {
          var targetH = (bdi.scrollHeight || bdi.offsetHeight || 360) + 16;
          bd.style.maxHeight = targetH + 'px';
        }
        mobileWrapper.classList.add('ymal-open');
        mobileWrapper.dataset.userClosed = 'false';
      }

      var drawerCartItems = mobileWrapper.closest('.cart-drawer__items--cart');
      if (drawerCartItems) {
        drawerCartItems.classList.toggle('ymal-cart-open', mobileWrapper.classList.contains('ymal-open'));
      }
    });

    setupScrollListener(mobileWrapper);
  }

  // Observe the mobile container for YMAL injection
  function observe() {
    var target = document.querySelector('.also-like-products--mobile') || document.body;
    var obs = new MutationObserver(function () {
      var wrapper = document.querySelector('.also-like-products--mobile');
      if (wrapper && wrapper.querySelector('.you-may-also-like')) {
        initToggle(wrapper);
      }
    });
    obs.observe(target, { childList: true, subtree: true });

    // Custom event listener for deterministic updates
    document.addEventListener('ymal:loaded', function () {
      var wrapper = document.querySelector('.also-like-products--mobile');
      if (wrapper) {
        initToggle(wrapper);
      }
    });

    // Also run immediately in case content is already present
    var wrapper = document.querySelector('.also-like-products--mobile');
    if (wrapper && wrapper.querySelector('.you-may-also-like')) {
      initToggle(wrapper);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
